import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AgentNumber from '../../../features/onboarding/AgentNumber';
import {
  type Checklist,
  type Forwarding,
  type LineType,
  forwardingCodes,
  nationalDigits,
  recommendedItems,
} from '../../../features/onboarding/onboardingChecklist';
import {
  useForwardingStatus,
  useGoLiveAction,
} from '../../../features/onboarding/useOnboardingChecklist';
import { useScopedQueryKey } from '../../../hooks/useScopedQueryKey';
import { formatPhone } from '../../../shared';
import {
  Button,
  Callout,
  Checkbox,
  Field,
  IconCheckCircle,
  Input,
  Reason,
  SUPPORT_EMAIL,
  Segmented,
  Spinner,
} from '../../../ui/v2';
import { type StepProps, StepIntro, StepPart, useStepFlow } from './StepFlow';

type GoLive = ReturnType<typeof useGoLiveAction>;

const Step: React.FC<{
  n: number;
  title: string;
  children: React.ReactNode;
}> = ({ n, title, children }) => (
  <StepPart n={n} title={title}>
    {children}
  </StepPart>
);

const at = (iso: string) => {
  const date = new Date(iso);
  const time = date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  return new Date().toDateString() === date.toDateString()
    ? `today at ${time}`
    : `on ${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${time}`;
};

/** "123 456 7890": easier to read off while dialling. */
const spaced = (number: string) => {
  const d = nationalDigits(number);
  return d.length === 10
    ? `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`
    : number;
};

// --- 1 · Ready to go live? ---------------------------------------------------

const ConfirmStep: React.FC<{ checklist: Checklist; goLive: GoLive }> = ({
  checklist,
  goLive,
}) => {
  const flow = useStepFlow();
  const [understood, setUnderstood] = useState(false);
  const [mainNumber, setMainNumber] = useState('');
  const needsMain = !checklist.main_number;
  const main = formatPhone(checklist.main_number || mainNumber);
  const transfer = checklist.items.find((i) => i.key === 'transfer_line');
  const open = recommendedItems(checklist);

  if (checklist.go_live_confirmed_at) {
    return (
      <Step n={1} title="Ready to go live?">
        <p className="flex items-center gap-2 text-v2-13 text-v2-fg-2">
          <IconCheckCircle size={16} className="text-v2-success" />
          You confirmed {at(checklist.go_live_confirmed_at)}.
        </p>
      </Step>
    );
  }

  return (
    <Step n={1} title="Ready to go live?">
      <ul className="mb-3 flex flex-col gap-1.5 text-v2-13 text-v2-fg-2">
        <li className="flex gap-2">
          <IconCheckCircle size={16} className="mt-px text-v2-success" />
          Set up is done
        </li>
        <li className="flex gap-2">
          <IconCheckCircle size={16} className="mt-px text-v2-success" />
          You tested your agent on {formatPhone(checklist.agent_number)} and
          said it sounded right
        </li>
        {transfer && (
          <li className="flex gap-2">
            <IconCheckCircle size={16} className="mt-px text-v2-success" />
            {transfer.detail}
          </li>
        )}
      </ul>
      {open.map((item) => (
        <Callout key={item.key} tone="warning" className="mb-3">
          {item.detail} You can go live anyway, or{' '}
          <button
            type="button"
            onClick={() => flow.open(item.key)}
            className="font-medium underline"
          >
            answer them first
          </button>
          .
        </Callout>
      ))}
      <Callout
        tone="neutral"
        title="Not on a VoIP phone system? You need two numbers."
        className="mb-3"
      >
        Your main number forwards every call to your agent, so your agent can’t
        transfer back to it; the call would come straight back. Transfers need a
        second number that rings at the restaurant, like a second line or a
        manager’s mobile.
      </Callout>
      <Callout
        tone="warning"
        title="From here, real calls go to your agent."
        className="mb-3"
      >
        As soon as forwarding is on, every call to {main || 'your main number'}{' '}
        is answered by your agent, not your staff.
      </Callout>
      {needsMain && (
        <Field
          label="The number guests call"
          hint="Your main number, the one on your website and Google listing. We check forwarding against it."
          className="mb-3 max-w-[320px]"
        >
          {(id, describedBy) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={mainNumber}
              onChange={(event) => setMainNumber(event.target.value)}
            />
          )}
        </Field>
      )}
      <Checkbox
        label={`I understand calls to ${main || 'my main number'} will go to my agent`}
        checked={understood}
        onChange={(event) => setUnderstood(event.target.checked)}
      />
      {goLive.isError && goLive.variables?.action === 'confirm' && (
        <Callout tone="danger" className="mt-3">
          {(goLive.error as Error).message}
        </Callout>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="primary"
          loading={goLive.isPending && goLive.variables?.action === 'confirm'}
          disabledReason={
            needsMain && nationalDigits(mainNumber).length !== 10
              ? 'Add the number guests call'
              : !understood
                ? 'Tick the box to confirm'
                : undefined
          }
          onClick={() =>
            goLive.mutate({
              action: 'confirm',
              ...(needsMain ? { main_number: mainNumber } : {}),
            })
          }
        >
          I’m ready, show me how
        </Button>
        <Button variant="ghost" onClick={flow.close}>
          Not yet
        </Button>
      </div>
    </Step>
  );
};

// --- 3 · Forwarding steps ----------------------------------------------------

const LINE_TYPES: Array<{ key: LineType; label: string }> = [
  { key: 'landline', label: 'Landline or business line' },
  { key: 'mobile', label: 'Mobile phone' },
  { key: 'voip', label: 'VoIP system' },
];

const instructions = (
  lineType: LineType,
  country: Forwarding['country'],
  agentNumber: string,
) => {
  const codes = forwardingCodes(lineType, agentNumber);
  const number = formatPhone(agentNumber);
  if (lineType === 'voip' || !codes) {
    return [
      'VoIP systems (RingCentral, Zoom Phone, 8x8 and others) don’t use star codes.',
      `In your provider’s admin portal, set your main number to forward every call to ${number}.`,
      'Some systems take a few minutes to apply the change.',
    ];
  }
  if (lineType === 'mobile') {
    return [
      `From the phone with your main number, dial ${codes.on} and press call.`,
      `To stop forwarding later, dial ${codes.off}.`,
    ];
  }
  return [
    `From the restaurant’s own phone, dial *72, then ${spaced(agentNumber)}. Wait for the confirmation tone, then hang up.`,
    ...(country === 'CA'
      ? [
          'On some lines (Bell), if nobody answers when you dial, hang up and dial the same code once more to confirm.',
          'No tone, or an error message? Call forwarding is a paid add-on on many Canadian lines. Ask your provider to turn it on.',
        ]
      : [
          'No tone, or an error message? Call forwarding is an add-on on some lines. Ask your provider to turn it on.',
        ]),
    `To stop forwarding later, dial ${codes.off}.`,
  ];
};

const ForwardingStep: React.FC<{ checklist: Checklist }> = ({ checklist }) => {
  const [country, setCountry] = useState<Forwarding['country']>(
    checklist.forwarding.country,
  );
  const [lineType, setLineType] = useState<LineType>('landline');
  const steps = instructions(lineType, country, checklist.agent_number);
  const mailto = `mailto:?subject=${encodeURIComponent(
    `Forward ${checklist.store_name}'s phone to our answering agent`,
  )}&body=${encodeURIComponent(
    [
      `Please forward every call to our main number${
        checklist.main_number ? ` (${formatPhone(checklist.main_number)})` : ''
      } to ${formatPhone(checklist.agent_number)}.`,
      '',
      ...steps,
    ].join('\n'),
  )}`;

  return (
    <Step n={3} title="Turn on forwarding with your phone provider">
      <p className="mb-3 text-v2-13 text-v2-fg-2">
        Forward every call. If you’d rather your staff answer first, tell your
        onboarding lead and we’ll set it up with you.
      </p>
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2 text-v2-12 text-v2-fg-3">
          Country
          <Segmented
            aria-label="Country"
            items={[
              { key: 'US', label: 'United States' },
              { key: 'CA', label: 'Canada' },
            ]}
            value={country}
            onChange={setCountry}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-v2-12 text-v2-fg-3">
          Your main number is a
          <Segmented
            aria-label="Line type"
            items={LINE_TYPES}
            value={lineType}
            onChange={setLineType}
          />
        </div>
      </div>
      <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-v2-13 text-v2-fg">
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="mt-3 text-v2-13 text-v2-fg-3">
        Can’t get it to work?{' '}
        <a href={mailto} className="font-medium text-v2-accent hover:underline">
          Send these steps to whoever manages your phones
        </a>
      </p>
    </Step>
  );
};

// --- 4 · Check that it works -------------------------------------------------

const CheckStep: React.FC<{ checklist: Checklist; goLive: GoLive }> = ({
  checklist,
  goLive,
}) => {
  const client = useQueryClient();
  const checklistKey = useScopedQueryKey('onboarding-checklist');
  const [phone, setPhone] = useState(checklist.forwarding.test_caller_number);
  const forwarding = useForwardingStatus();
  const status = forwarding.data?.forwarding ?? checklist.forwarding;
  const live = Boolean(forwarding.data?.live_at ?? checklist.live_at);

  useEffect(() => {
    if (live && !checklist.live_at) {
      void client.invalidateQueries({ queryKey: checklistKey });
    }
  }, [live, checklist.live_at, client, checklistKey]);

  const main = formatPhone(checklist.main_number) || 'your main number';
  // One step at a time: the clicked button spins, the others wait for it.
  const stepProps = (action: string) => ({
    loading: goLive.isPending && goLive.variables?.action === action,
    disabledReason:
      goLive.isPending && goLive.variables?.action !== action
        ? 'Saving your last answer'
        : undefined,
  });

  let state: React.ReactNode;
  if (live) {
    const forwardedFrom = status.forwarded_from || checklist.main_number;
    const callId = status.live_call_id || checklist.forwarding.live_call_id;
    state = (
      <Callout
        tone="accent"
        icon={<IconCheckCircle size={14} />}
        title={`Forwarding works. ${checklist.store_name} is live.`}
      >
        {forwardedFrom
          ? `Your agent answered a call forwarded from ${formatPhone(forwardedFrom)}.`
          : 'Your agent is answering your calls.'}
        <div className="mt-2 flex flex-wrap gap-2">
          <Link to="/">
            <Button variant="primary" size="sm">
              Go to your dashboard
            </Button>
          </Link>
          {callId && (
            <Link to={`/calls?call_id=${encodeURIComponent(callId)}`}>
              <Button size="sm">Open the call</Button>
            </Link>
          )}
        </div>
      </Callout>
    );
  } else if (!checklist.can_confirm && !status.checking) {
    // Staff can follow along, but owners and managers run the check.
    state = (
      <Reason>
        Your store’s owner or a manager runs this check from this page.
      </Reason>
    );
  } else if (status.pending_forwarded_from && checklist.can_confirm) {
    state = (
      <Callout
        tone="warning"
        title={`We saw a forwarded call from ${formatPhone(status.pending_forwarded_from)}.`}
      >
        Is that your main number?
        {checklist.main_number && ` We have ${main} on file.`}
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            {...stepProps('accept_number')}
            onClick={() => goLive.mutate({ action: 'accept_number' })}
          >
            Yes, that’s my main number
          </Button>
          <Button
            size="sm"
            {...stepProps('reject_number')}
            onClick={() => goLive.mutate({ action: 'reject_number' })}
          >
            No, keep waiting
          </Button>
        </div>
      </Callout>
    );
  } else if (status.pending_test_call && checklist.can_confirm) {
    state = (
      <Callout
        tone="warning"
        title={`We got a call from ${
          formatPhone(status.test_caller_number) || 'your phone'
        }.`}
      >
        Did you dial your main number, {main}? A call straight to the toll-free
        number doesn’t show that forwarding works.
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="primary"
            {...stepProps('accept_call')}
            onClick={() => goLive.mutate({ action: 'accept_call' })}
          >
            Yes, I dialled {main}
          </Button>
          <Button
            size="sm"
            {...stepProps('reject_call')}
            onClick={() => goLive.mutate({ action: 'reject_call' })}
          >
            No, I’ll call again
          </Button>
        </div>
      </Callout>
    );
  } else if (status.checking) {
    state = (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-v2 border border-v2-line px-3 py-2.5">
        <div className="flex items-center gap-2.5">
          <Spinner />
          <div>
            <p className="text-v2-13 font-medium text-v2-fg">
              Waiting for a forwarded call…
            </p>
            <p className="text-v2-12 text-v2-fg-3">
              We’ll keep watching for 15 minutes. You can leave this page.
            </p>
          </div>
        </div>
        {checklist.can_confirm && (
          <Button
            size="sm"
            variant="ghost"
            {...stepProps('stop_check')}
            onClick={() => goLive.mutate({ action: 'stop_check' })}
          >
            Stop checking
          </Button>
        )}
      </div>
    );
  } else {
    const ran = Boolean(status.check_started_at);
    state = (
      <>
        {ran && (
          <Callout
            tone="warning"
            title="We haven’t seen a forwarded call yet."
            className="mb-3"
          >
            <ul className="list-disc pl-4">
              <li>
                Call the restaurant’s number, {main}, not the toll-free one.
              </li>
              <li>Dial the code from the restaurant line itself.</li>
              <li>
                Call from the phone you entered below, so we recognise it.
              </li>
              <li>VoIP systems can take a few minutes to apply a change.</li>
            </ul>
          </Callout>
        )}
        <Field
          label="Which phone will you call from?"
          hint="Some phone companies don’t pass your number along. This lets us recognise your call anyway."
          optional
          className="max-w-[320px]"
        >
          {(id, describedBy) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          )}
        </Field>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            {...stepProps('start_check')}
            disabledReason={
              phone.trim() && nationalDigits(phone).length !== 10
                ? 'Enter a 10-digit number, or leave it blank'
                : stepProps('start_check').disabledReason
            }
            onClick={() =>
              goLive.mutate({
                action: 'start_check',
                test_caller_number: phone.trim(),
              })
            }
          >
            {ran ? 'Check again' : 'Start checking'}
          </Button>
          {ran && (
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                `Forwarding help for ${checklist.store_name}`,
              )}`}
              className="text-v2-13 font-medium text-v2-accent hover:underline"
            >
              Get help
            </a>
          )}
        </div>
      </>
    );
  }

  return (
    <Step n={4} title="Check that it works">
      {!live && (
        <p className="mb-3 text-v2-13 text-v2-fg-2">
          From a different phone, call {main}. When we see the forwarded call,{' '}
          {checklist.store_name} is live.
        </p>
      )}
      {goLive.isError && goLive.variables?.action !== 'confirm' && (
        <Callout tone="danger" className="mb-3">
          {(goLive.error as Error).message}
        </Callout>
      )}
      {state}
    </Step>
  );
};

// --- the step ----------------------------------------------------------------

/**
 * Go live, inside its Getting started step: confirm, the agent's number,
 * the forwarding codes, then the check that a forwarded call arrived.
 */
export default function GoLiveStep({
  checklist,
}: Pick<StepProps, 'checklist'>) {
  const goLive = useGoLiveAction();
  const confirmed = Boolean(checklist.go_live_confirmed_at);
  const live = checklist.status === 'live';
  const main = formatPhone(checklist.main_number);

  if (!live && !confirmed && checklist.status !== 'ready') {
    return (
      <Reason>
        Going live unlocks when everything above is done, including steps
        waiting on us.
      </Reason>
    );
  }
  if (!checklist.can_confirm && !live && !confirmed) {
    return (
      <Reason>
        Your store’s owner or a manager goes live. Ask them to open Getting
        started.
      </Reason>
    );
  }
  // Once confirmed, staff see the same steps; only the actions are held back.
  return (
    <>
      {!live && (
        <StepIntro>
          The last step. Once your number is forwarded, guests calling{' '}
          {main || 'your main number'} reach your agent. Your number, your
          Google listing and your menus don’t change, and you can turn
          forwarding off at any time.
        </StepIntro>
      )}
      {!live && <ConfirmStep checklist={checklist} goLive={goLive} />}
      {(confirmed || live) && (
        <>
          {!live && (
            <>
              <Step n={2} title="Your agent’s number">
                <p className="mb-2 text-v2-13 text-v2-fg-2">
                  The same toll-free number you tested on. Calls are forwarded
                  here. Don’t publish it; guests keep calling your usual number.
                </p>
                <AgentNumber number={checklist.agent_number} />
              </Step>
              <ForwardingStep checklist={checklist} />
            </>
          )}
          <CheckStep checklist={checklist} goLive={goLive} />
        </>
      )}
    </>
  );
}
